<template>
<div
  class="player"
  :class="{minimized}"
  :style="{left:posX+'px',top:posY+'px'}"
>

  <div class="header" @mousedown="startDrag">
    🎧 Dictation
    <button @click.stop="toggleMin">
      {{ minimized ? '+' : '-' }}
    </button>
  </div>

  <div v-if="!minimized" class="body">

    <audio
      ref="audio"
      :src="src"
      preload="auto"
    ></audio>

    <div class="controls">

      <button @click="toggle">
        {{ playing ? 'Pause' : 'Play' }}
      </button>

      <button @click="rewind">-3s</button>
      <button @click="forward">+3s</button>

      <span class="time">
        {{ format(current) }}
      </span>

      <select v-model="speed" @change="setSpeed">
        <option value="0.75">0.75x</option>
        <option value="1">1x</option>
        <option value="1.25">1.25x</option>
        <option value="1.5">1.5x</option>
        <option value="2">2x</option>
      </select>

    </div>

  </div>

</div>
</template>

<script>
export default {

    props:{
        src:String
    },

    data(){
        return{

            audio:null,

            playing:false,
            current:0,

            speed:1,

            minimized:false,

            posX:100,
            posY:120,

            dragging:false,
            offsetX:0,
            offsetY:0,

            pedalCenter:false,
            pedalRight:false,

            audioCtx:null,
            analyser:null,
            data:null,
            lastSilence:0,
            silenceStart:null

        }
    },

    mounted(){

        this.audio = this.$refs.audio

        const x = localStorage.getItem("dictPlayerX")
        const y = localStorage.getItem("dictPlayerY")

        if(x) this.posX=parseInt(x)
        if(y) this.posY=parseInt(y)

        this.audio.addEventListener("timeupdate",()=>{
            this.current=this.audio.currentTime
        })

        document.addEventListener("mousemove",this.drag)
        document.addEventListener("mouseup",this.stopDrag)

        document.addEventListener("keydown",this.pedalDown)
        document.addEventListener("keyup",this.pedalUp)

        document.addEventListener("keydown",this.shortcuts)

        this.initAudioAnalysis()

    },

    beforeDestroy(){

        document.removeEventListener("mousemove",this.drag)
        document.removeEventListener("mouseup",this.stopDrag)

        document.removeEventListener("keydown",this.pedalDown)
        document.removeEventListener("keyup",this.pedalUp)

        document.removeEventListener("keydown",this.shortcuts)

    },

    methods:{

        toggle(){

            if(this.audio.paused){

                this.audio.play()
                this.playing=true

            }else{

                this.smartPause()

            }

        },

        smartPause(){

            this.audio.pause()

            if(this.lastSilence){

                this.audio.currentTime=this.lastSilence

            }

            this.playing=false

        },

        rewind(){

            this.audio.currentTime=Math.max(
                0,
                this.audio.currentTime-3
            )

        },

        forward(){

            this.audio.currentTime=Math.min(
                this.audio.duration,
                this.audio.currentTime+3
            )

        },

        setSpeed(){

            this.audio.playbackRate=this.speed

        },

        format(sec){

            sec=Math.floor(sec)

            const m=Math.floor(sec/60)
            const s=sec%60

            return `${m}:${s.toString().padStart(2,'0')}`

        },

        toggleMin(){

            this.minimized=!this.minimized

        },

        startDrag(e){

            this.dragging=true

            this.offsetX=e.clientX-this.posX
            this.offsetY=e.clientY-this.posY

        },

        drag(e){

            if(!this.dragging) return

            this.posX=e.clientX-this.offsetX
            this.posY=e.clientY-this.offsetY

        },

        stopDrag(){

            if(!this.dragging) return

            this.dragging=false

            localStorage.setItem("dictPlayerX",this.posX)
            localStorage.setItem("dictPlayerY",this.posY)

        },

        shortcuts(e){

            if(!e.ctrlKey) return

            switch(e.key){

                case " ":
                    e.preventDefault()
                    this.toggle()
                    break

                case "ArrowLeft":
                    this.rewind()
                    break

                case "ArrowRight":
                    this.forward()
                    break

            }

        },

        pedalDown(e){

            switch(e.code){

                case "F8":

                if(this.pedalCenter) return
                this.pedalCenter=true

                this.audio.play()
                this.playing=true

                break

                case "F7":

                this.rewind()
                break

                case "F9":

                if(this.pedalRight) return
                this.pedalRight=true

                this.audio.playbackRate=1.75

                break

            }

        },

        pedalUp(e){

            switch(e.code){

                case "F8":

                    this.pedalCenter=false

                    this.smartPause()

                    break

                case "F9":

                    this.pedalRight=false

                    this.audio.playbackRate=this.speed

                    break

            }

        },

        initAudioAnalysis(){

            const AudioContext= window.AudioContext|| window.webkitAudioContext;

            this.audioCtx=new AudioContext();

            const src=this.audioCtx.createMediaElementSource(this.audio)

            this.analyser=this.audioCtx.createAnalyser()

            src.connect(this.analyser)
            this.analyser.connect(this.audioCtx.destination)

            this.data=new Uint8Array(this.analyser.fftSize)

            this.detectSilence()

        },

        detectSilence(){

            this.analyser.getByteTimeDomainData(this.data)

            let sum=0

            for(let i=0;i<this.data.length;i++){

                const v=(this.data[i]-128)/128
                sum+=v*v

            }

            const volume=Math.sqrt(sum/this.data.length)

            const threshold=0.02

            if(volume<threshold){

                if(!this.silenceStart){

                this.silenceStart=this.audio.currentTime

                }

                if(this.audio.currentTime-this.silenceStart>0.3){

                this.lastSilence=this.silenceStart

                }

            }else{

                this.silenceStart=null

            }

            requestAnimationFrame(this.detectSilence)

        }

    }

}
</script>

<style scoped>
.player{

position:fixed;
width:300px;

background:white;

border-radius:10px;

box-shadow:0 10px 30px rgba(0,0,0,0.25);

z-index:9999;

font-family:Arial;

}

.header{

background:#2563eb;
color:white;

padding:8px;

display:flex;
justify-content:space-between;

cursor:move;

border-radius:10px 10px 0 0;

}

.body{

padding:10px;

}

.controls{

display:flex;
gap:6px;
align-items:center;

}

.time{

font-size:12px;
width:50px;

}

button{

padding:4px 8px;
cursor:pointer;

}

.minimized{

width:150px;

}
</style>